import React, { useContext, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Divider, ListItem, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useFormStyles } from "./formFields";
import { FormContext } from "./form";
import DeleteIcon from "@material-ui/icons/Delete";
import { getLocalizedNameWithIntl } from "../../globals/langHelpers";
import { Autocomplete } from "@material-ui/lab";

/*
    Form element for Complete job biling page - activity/SKU selection
    props:
        - activity: object, activity object being edited with comment and quantity
        - editorProps: (props) => input, the input field for the form element
        - onRemove: (activity) => void, callback to remove item from rendered list 
                        of selected billing activities
        - required: bool, whether or not the form element is required
        - characterCountEnabled: bool, whether or not the form element will display a character counter
*/

export const activityUseStyles = makeStyles((theme) => ({
    formResult: {
        display: "flex",
        padding: "13px 0px",
    },
    quantity: {
        "& input": {
            padding: "12px",
        },
    },
    formQtyDelete: {
        margin: "auto 0 auto",
        display: "flex",
        padding: 2,
        paddingTop: 35,
    },
    formDelete: {
        marginTop: "8px",
    },
    valueDetails: {
        wordBreak: "break-word",
    },
    unitOfMeasure: {
        textAlign: "right",
        fontSize: "10px",
        color: "#999999",
    },
}));

export const ActivityFormField = ({
    activity,
    editorProps,
    onRemove,
    billingReasons,
    verifyActivities,
    required = false,
    characterCountEnabled = false,
}) => {
    const classes = Object.assign(activityUseStyles(), useFormStyles());
    const formServices = useContext(FormContext);
    const [valueDetails, setValueDetails] = useState(activity.additionalDetails);
    const [selectedBillingReason, setSelectedBillingReason] = useState(activity.billingReason);
    const [quantityError, setQuantityError] = useState(true);
    const intl = useIntl();

    const onChange = (event) => {
        const val = event.target.value;
        activity.additionalDetails = val;
        setValueDetails(val);
        verifyActivities();
    };
    const onQuantityChange = (event) => {
        const validator = /^(?:\d*\.\d{1,2}|\d+)$/;
        let value = parseFloat(event.target.value);
        if (validator.test(value)) {
            activity.quantity = value;
        } else {
            let roundedValue = parseFloat(value).toFixed(2);
            event.target.value = activity.quantity = roundedValue;
        }
        verifyActivities();
        setQuantityError(isNaN(parseFloat(activity.quantity)) || parseFloat(activity.quantity) <= 0);
    };

    const labels = defineMessages({
        close: {
            id: "admin.work-order.close",
            defaultMessage: "Close",
            description: "Admin work order close text",
        },
        open: {
            id: "admin.work-order.open",
            defaultMessage: "Open",
            description: "Admin work order open",
        },
        noOptions: {
            id: "admin.work-order.no-options",
            defaultMessage: "No options",
            description: "Admin work order no options",
        },
        billingReason: {
            id: "labourer-billing.reason",
            defaultMessage: "Please provide a billing reason",
            description: "labourer billing reason",
        },
    });

    return (
        <>
            <ListItem>
                <div className={classes.formElement}>
                    <div className={classes.formInfo}>
                        <div className={classes.infoHeader}>{getLocalizedNameWithIntl(intl, { object: activity, value: "productDescription" })}</div>
                        <div className={classes.formResult}>
                            {editorProps &&
                                formServices.edit({
                                    ...editorProps,
                                    required,
                                    value: valueDetails,
                                    onChange,
                                    activity: true,
                                    characterCount: characterCountEnabled ? (valueDetails ? valueDetails.length : "0") : null,
                                })}
                        </div>
                        {valueDetails ? <div className={classes.valueDetails}>{valueDetails}</div> : ""}
                    </div>
                    {activity.isBillingReasonRequired && (
                        <div className={classes.formInfo}>
                            <div className={classes.required}>{intl.formatMessage(labels.billingReason)}</div>
                            <div>
                                <Autocomplete
                                    id="complete-billing-reasons"
                                    fullWidth
                                    closeText={intl.formatMessage(labels.close)}
                                    openText={intl.formatMessage(labels.open)}
                                    noOptionsText={intl.formatMessage(labels.noOptions)}
                                    options={billingReasons}
                                    getOptionLabel={(option) => getLocalizedNameWithIntl(intl, { object: option, value: "name" })}
                                    onChange={(event, newValue) => {
                                        setSelectedBillingReason(newValue);
                                        activity.billingReason = newValue;
                                        verifyActivities();
                                    }}
                                    renderInput={(params) => <TextField {...params} size="small" variant="outlined" error={!selectedBillingReason} />}
                                />
                            </div>
                        </div>
                    )}
                    <div className={classes.formQtyDelete}>
                        <div>
                            <TextField
                                id={"quantity-" + activity.id}
                                error={quantityError}
                                className={classes.quantity}
                                InputProps={{
                                    pattern: "[0-9]*\\.?[0-9]*",
                                    inputProps: { min: 0.01, step: 0.01 },
                                }}
                                variant="outlined"
                                type="number"
                                onChange={onQuantityChange}
                                placeholder={`0 ${activity.unitOfMeasure}`}
                                style={{ width: "75px" }}
                            />
                            <div className={classes.unitOfMeasure}>{activity.unitOfMeasure}</div>
                        </div>
                        <DeleteIcon color="primary" className={classes.formDelete} onClick={onRemove} />
                    </div>
                </div>
            </ListItem>
            <Divider />
        </>
    );
};

export default ActivityFormField;
